import { useState, FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Star } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

type Currency = {
  id: number
  code: string
  name: string
  symbol: string
  decimal_places: number
  is_base: boolean
  is_active: boolean
  is_system: boolean
  sort_order: number
}

type CurrencyInput = Omit<Currency, 'id' | 'is_system'>

const emptyCurrency: CurrencyInput = {
  code: '', name: '', symbol: '',
  decimal_places: 2, is_base: false, is_active: true, sort_order: 100,
}

export default function FinanceCurrencies() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['finance', 'currencies'],
    queryFn: async () => (await api.get<Currency[]>('/finance/currencies/')).data,
  })

  const [editing, setEditing] = useState<Currency | null>(null)
  const [creating, setCreating] = useState(false)

  const createMut = useMutation({
    mutationFn: async (p: CurrencyInput) => (await api.post('/finance/currencies/', p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['finance', 'currencies'] }); setCreating(false) },
  })
  const updateMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: CurrencyInput }) =>
      (await api.patch(`/finance/currencies/${id}/`, payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['finance', 'currencies'] }); setEditing(null) },
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/finance/currencies/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['finance', 'currencies'] }),
  })

  const currencies = data || []

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Currencies</h2>
          <p className="text-sm text-gray-500">
            One currency is marked as <strong>base</strong>: that's the company's reporting currency.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>+ Add currency</Button>
      </div>

      {isLoading ? <p className="text-gray-500">Loading…</p> : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Symbol</Th>
                <Th className="text-center">Decimals</Th>
                <Th>Base</Th>
                <Th>Active</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currencies.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <Td className="font-mono">{c.code}</Td>
                  <Td>{c.name} {c.is_system && <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">sys</span>}</Td>
                  <Td>{c.symbol}</Td>
                  <Td className="text-center">{c.decimal_places}</Td>
                  <Td>{c.is_base && (
                    <span className="text-green-700 font-bold inline-flex items-center gap-1">
                      <Star className="h-3.5 w-3.5 fill-current" /> BASE
                    </span>
                  )}</Td>
                  <Td><span className={`inline-block w-2 h-2 rounded-full ${c.is_active ? 'bg-green-500' : 'bg-gray-300'}`} /></Td>
                  <Td>
                    <button onClick={() => setEditing(c)} className="text-blue-600 hover:underline text-sm mr-3">Edit</button>
                    {!c.is_system && !c.is_base && (
                      <button onClick={() => { if (confirm(`Delete ${c.code}?`)) deleteMut.mutate(c.id) }}
                        className="text-red-600 hover:underline text-sm">Delete</button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <CurrencyDialog
          initial={editing ? { ...editing } : emptyCurrency}
          isEdit={!!editing}
          submitting={createMut.isPending || updateMut.isPending}
          error={(createMut.error as any) || (updateMut.error as any)}
          onCancel={() => { setCreating(false); setEditing(null) }}
          onSubmit={(p) => {
            if (editing) updateMut.mutate({ id: editing.id, payload: p })
            else createMut.mutate(p)
          }}
        />
      )}
    </AdminLayout>
  )
}

function CurrencyDialog({
  initial, isEdit, onSubmit, onCancel, submitting, error,
}: {
  initial: CurrencyInput
  isEdit: boolean
  onSubmit: (p: CurrencyInput) => void
  onCancel: () => void
  submitting: boolean
  error: any
}) {
  const [form, setForm] = useState<CurrencyInput>(initial)
  const set = <K extends keyof CurrencyInput>(k: K, v: CurrencyInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const submit = (e: FormEvent) => { e.preventDefault(); onSubmit(form) }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{isEdit ? 'Edit currency' : 'Add currency'}</h3>
        <form onSubmit={submit}>
          <FieldSet>
            <FieldLegend>Currency</FieldLegend>
            <FieldDescription>Define an ISO-4217 currency for the company books.</FieldDescription>
            <FieldGroup>
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <FieldLabel htmlFor="currency-code">Code (ISO 4217)</FieldLabel>
                  <Input
                    id="currency-code"
                    type="text" required maxLength={3} value={form.code}
                    onChange={(e) => set('code', e.target.value.toUpperCase())}
                    placeholder="USD"
                    className="font-mono"
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="currency-symbol">Symbol</FieldLabel>
                  <Input
                    id="currency-symbol"
                    type="text" maxLength={8} value={form.symbol}
                    onChange={(e) => set('symbol', e.target.value)}
                    placeholder="$"
                  />
                </Field>
              </div>
              <Field>
                <FieldLabel htmlFor="currency-name">Name</FieldLabel>
                <Input
                  id="currency-name"
                  type="text" required value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="US Dollar"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <FieldLabel htmlFor="currency-decimals">Decimal places</FieldLabel>
                  <Input
                    id="currency-decimals"
                    type="number" min={0} max={4} value={form.decimal_places}
                    onChange={(e) => set('decimal_places', Number(e.target.value))}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="currency-sort">Sort order</FieldLabel>
                  <Input
                    id="currency-sort"
                    type="number" value={form.sort_order}
                    onChange={(e) => set('sort_order', Number(e.target.value))}
                  />
                </Field>
              </div>
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked={form.is_base}
                  onChange={(e) => set('is_base', e.target.checked)} />
                <span className="text-sm text-gray-700">Base currency (reporting currency — only one allowed)</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked={form.is_active}
                  onChange={(e) => set('is_active', e.target.checked)} />
                <span className="text-sm text-gray-700">Active</span>
              </label>

              {error && (
                <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded">
                  {String(error?.response?.data ? JSON.stringify(error.response.data) : error.message || error)}
                </div>
              )}
            </FieldGroup>
          </FieldSet>

          <div className="flex justify-end gap-2 pt-3">
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide ${className || ''}`}>{children}</th>
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-sm ${className || ''}`}>{children}</td>
}
